import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { RowSelectionState, DropdownItem } from 'design-system-web';
import { bulkAssignTeamsMutation } from 'graphql-queries';
import { Dropdown } from 'components';
import { Assignable } from 'domains-work-order-type-contracts';

/** @deprecated Use Assignable instead */
type AdminUser = Assignable;

const SERVICE_REQUEST_SELECTION_LIMIT = 100;

type Props = {
  rowSelection?: RowSelectionState;
  assignableTeams: AdminUser[];
  refetch?: () => void;
};

export function BulkTeamsAssignAction({
  rowSelection,
  assignableTeams,
  refetch,
}: Props) {
  const { t } = useTranslation();
  const [bulkAssignTeams] = useMutation(bulkAssignTeamsMutation);

  const handleTeamsAssign = async (teams: AdminUser[]) => {
    if (!teams || teams.length === 0 || !rowSelection) return;

    const selectedWorkOrderIds = Object.entries(rowSelection)
      .filter(([_, isSelected]) => isSelected)
      .map(([workOrderId]) => workOrderId);

    if (selectedWorkOrderIds.length === 0) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignTeams.noSelection')
      );
      return;
    }

    if (selectedWorkOrderIds.length > SERVICE_REQUEST_SELECTION_LIMIT) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignTeams.limitExceeded', {
          serviceRequestSelectionLimit: SERVICE_REQUEST_SELECTION_LIMIT,
          selectedWorkOrderCount: selectedWorkOrderIds.length,
        }),
        5000
      );
      return;
    }

    // Confirm action with user
    try {
      await window.Alert.confirm({
        title: t(
          'web.admin.serviceRequest.bulkActions.assignTeams.confirm.title'
        ),
        message: t(
          'web.admin.serviceRequest.bulkActions.assignTeams.confirm.message',
          {
            count: selectedWorkOrderIds.length,
            teamCount: 1,
          }
        ),
        confirmText: t(
          'web.admin.serviceRequest.bulkActions.assignTeams.confirm.button'
        ),
        cancelText: t(
          'web.admin.serviceRequest.bulkActions.assignTeams.cancel.button'
        ),
      });
    } catch {
      // User cancelled - no state to clean up
      return;
    }

    window.Alert.loading({
      title: t('web.admin.serviceRequest.bulkActions.assignTeams.inProgress'),
      disableCloseOnBackgroundClick: true,
    });

    try {
      const assigneeGroups = teams.map((team: AdminUser) => team.value);

      const result = await bulkAssignTeams({
        variables: {
          serviceRequestIds: selectedWorkOrderIds,
          assigneeGroups,
        },
      });

      const { successCount, failureCount } = result.data?.bulkAssignTeams || {
        successCount: 0,
        failureCount: selectedWorkOrderIds.length,
      };

      window.Alert.hide();

      if (failureCount === 0) {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignTeams.success', {
            count: successCount,
          })
        );
      } else if (successCount === 0) {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignTeams.error')
        );
      } else {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignTeams.partial', {
            successCount,
            failureCount,
          }),
          5000
        );
      }

      if (refetch) {
        refetch();
      }
    } catch {
      window.Alert.hide();
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignTeams.error')
      );
    }
  };

  return (
    <div style={{ minWidth: '200px' }}>
      <Dropdown
        isSearchable
        value=""
        items={assignableTeams || []}
        onChange={(item: DropdownItem<string>) => {
          if (item?.value) {
            const selectedTeam = assignableTeams.find(
              team => team.value === item.value
            );
            if (selectedTeam) {
              handleTeamsAssign([selectedTeam]);
            }
          }
        }}
        placeholder={t(
          'web.admin.serviceRequest.bulkActions.assignTeams.selectTeams'
        )}
        doTranslation={false}
        testId="bulk-sr-teams-assign-select"
      />
    </div>
  );
}
